// v0.6.10 — one place that turns an engine code into words.
//
// WHY THIS FILE EXISTS — the operator kept meeting the engine's own
// vocabulary on screen:
//
//   TraceDrawer         GB · 20% · STANDARD
//   ViesHeldQueue       BUYER_VAT_NUMBER_MISSING, GB_TO_EU_ZERO_RATED_EXPORT
//   AmendmentRow        ACTION_REQUIRED
//   RatePendingAcks     PT · REDUCED
//   ClientHistory       EU_OSS · RATE_DRIFT
//
// None of these are bugs in isolation, and that is exactly the
// problem: SIX components had each grown their own private
// `const X_LABELS = {...}` map (ReconciliationPanel, RefundAuditView,
// RefundChannelResolver, DuplicateFileModal, FilingPackagePanel,
// ClientEditForm), so the pattern was known and applied — just never
// in the seventh place. A shared module plus the Rule 5 gate in
// `backend/ux_consistency_rules.py` makes "render the raw code" the
// thing you have to go out of your way to do.
//
// A missing dictionary entry is NOT a raw code: `humanise` still
// produces readable words, so a code added tomorrow degrades to
// "Art 59a routed to use enjoyment country", never to the enum.

// Acronyms the operator reads as acronyms — they must survive
// humanise() in upper case rather than becoming "Oss" or "B2b".
const ACRONYMS = new Set([
  "VAT", "OSS", "IOSS", "EU", "UK", "GB", "B2B", "B2C", "ICS", "VOEC",
  "FBA", "FC", "RC", "TBE", "MSC", "VIES", "XLSX", "CSV", "ID", "NI",
]);

/**
 * Last-resort rendering for a code with no dictionary entry.
 * "ART59A_ROUTED_TO_USE_ENJOYMENT_COUNTRY"
 *   -> "Art 59a routed to use enjoyment country"
 */
export function humanise(code) {
  if (code === null || code === undefined || code === "") return "—";
  const raw = String(code);
  // A value that is already prose (a country name, a client's own
  // label) must pass through untouched.
  if (!/^[A-Z][A-Z0-9_]*$/.test(raw)) return raw;

  const words = raw.split("_").filter(Boolean).map((w) => {
    if (ACRONYMS.has(w)) return w;
    // "ART59A" / "ART44" — the article number is the useful part.
    const art = w.match(/^ART(\d+[A-Z]?)$/);
    if (art) return `Art ${art[1].toLowerCase()}`;
    return w.toLowerCase();
  });
  const [first, ...rest] = words;
  const head = ACRONYMS.has(first) || first.startsWith("Art ")
    ? first
    : first.charAt(0).toUpperCase() + first.slice(1);
  return [head, ...rest].join(" ");
}

export const TAX_CATEGORY = {
  STANDARD: "Standard rate",
  REDUCED: "Reduced rate",
  SUPER_REDUCED: "Super-reduced rate",
  ZERO: "Zero-rated",
  EXEMPT: "Exempt",
  REVERSE_CHARGE: "Reverse charge",
  OUT_OF_SCOPE: "Outside the scope",
  STANDARD_SHIPPING: "Standard rate (shipping)",
  // v0.6.12 — the country-specific reduced tiers. Art 98(1) (as amended
  // by Directive (EU) 2022/542) lets a Member State run two reduced
  // rates, so "Reduced rate" alone cannot name which one a row is.
  // The numbered labels carry their rate because the key does too
  // (AT REDUCED_13 = 13%, HU REDUCED_5 = 5% / REDUCED_18 = 18%); the
  // named ones use each country's own term for the tier.
  INTERMEDIATE: "Intermediate rate",
  MINIMUM: "Minimum rate",
  PARKING: "Parking rate",
  REDUCED_5: "Reduced rate (5%)",
  REDUCED_13: "Reduced rate (13%)",
  REDUCED_18: "Reduced rate (18%)",
};

export const REGIME = {
  // v0.6.12 — EU_LOCAL was missing, so RegimesApplied rendered the raw code
  // on screen ("EU_LOCAL · 9 TXNS"). Operator-reported.
  EU_LOCAL: "Local national return",
  UK_VAT: "UK VAT return",
  EU_OSS: "EU One-Stop-Shop",
  EU_NON_UNION_OSS: "Non-Union One-Stop-Shop",
  IOSS: "Import One-Stop-Shop",
  NATIONAL: "National return",
};

export const ADAPTER = {
  shopify: "Shopify",
  woocommerce: "WooCommerce",
  amazon: "Amazon",
  ebay: "eBay",
  ebay_seller_transactions: "eBay (seller transactions)",
  client_template: "Client template",
};

export const PRODUCT_TYPE = {
  GOODS: "Goods",
  SERVICES: "Services",
  DIGITAL: "Digital",
};

// Moved here from ReconciliationPanel.jsx, which held the only copy.
export const BUCKET = {
  LOCAL_SALE: "Local taxable",
  REVERSE_CHARGE: "Reverse charge",
  B2B_ICS: "B2B ICS (EC Sales List)",
  B2B_ICS_CANDIDATE: "B2B ICS (pending VIES)",
  B2B_ICS_PENDING: "B2B ICS (VIES unavailable)",
  DISTANCE_SALE: "OSS distance sale",
  OSS_DISTANCE_SALE: "OSS distance sale",
  OSS_SERVICE: "OSS service",
  EU_NON_UNION_OSS: "Non-Union OSS",
  EXPORT: "Export (outside EU)",
  MARKETPLACE: "Marketplace deemed supplier",
  IOSS_SCHEME: "IOSS scheme",
  CH_VOEC: "CH VOEC",
  JE_VOEC: "JE VOEC",
  FC_TRANSFER: "Fulfilment-centre transfer",
  INBOUND_NON_SALE: "Inbound non-sale",
  // Sweep A5-23 (2026-10-11): the only stored bucket with no label, so the
  // screen showed the raw code. VAT on these is collected at the border.
  IMPORT_STANDARD: "Import (VAT paid at the border)",
  REFUND_B2B: "Refund (B2B)",
  REFUND_B2C: "Refund (B2C)",
  UNCLASSIFIED: "Unclassified — held",
};

export const AMENDMENT_STATUS = {
  OPEN: "Open",
  ACTION_REQUIRED: "Action required",
  IN_PROGRESS: "In progress",
  SUBMITTED: "Submitted",
  CLOSED: "Closed",
  SUPERSEDED: "Superseded",
};

export const TRIGGER_KIND = {
  RATE_DRIFT: "Rate changed after filing",
  RATE_CORRECTION: "Rate corrected",
  OPERATOR_AMENDMENT: "Operator amendment",
  SUPERSEDED_CALC: "Calculation superseded",
};

export const TRANSACTION_TYPE = {
  SALE: "Sale",
  REFUND: "Refund",
  RETURN: "Return",
};

// `supply_type` is deliberately absent: its labels cite VAT articles
// and must be translatable, so they live in i18n under `supply_types`
// and are read with t(). Rule 6 still polices the field; the call site
// uses t(`supply_types.${v}`), which the rule accepts.
// v0.6.12 — the client's own two enums, which were reaching the screen RAW.
// The operator saw "NON_EU" beside their client's name on the calculate
// screen and "NON_EU_PAN_EU_FBA" / "LOCAL_RETURNS" in the admin list.
//
// A correct map already existed — privately, inside ClientEditForm.jsx, where
// its comment even says an operator must not see "NON_EU_PAN_EU_FBA". Three
// other render sites never got it. That is the drift this module exists to
// prevent, so the map lives here now and ClientEditForm imports it.
//
// Values are Literal sets in backend/models/client.py:41-42; keep in step.
export const ESTABLISHMENT = {
  EU: "EU-established",
  NON_EU: "Non-EU-established",
  NON_EU_PAN_EU_FBA: "Non-EU, Pan-EU FBA",
};

export const FILING_MODEL = {
  UNION_OSS: "Union OSS",
  LOCAL_RETURNS: "Local returns per country",
  HYBRID: "Hybrid (OSS + local)",
};

const DICTIONARIES = {
  tax_category: TAX_CATEGORY,
  rate_type: TAX_CATEGORY,
  inherited_bucket: BUCKET,
  transaction_type: TRANSACTION_TYPE,
  regime: REGIME,
  adapter: ADAPTER,
  product_type: PRODUCT_TYPE,
  bucket: BUCKET,
  amendment_status: AMENDMENT_STATUS,
  trigger_kind: TRIGGER_KIND,
  seller_establishment: ESTABLISHMENT,
  filing_model: FILING_MODEL,
};

/**
 * `labelFor("tax_category", "STANDARD_SHIPPING")` -> "Standard rate (shipping)"
 * `labelFor("bucket", "SOME_NEW_CODE")`           -> "Some new code"
 */
export function labelFor(kind, value) {
  if (value === null || value === undefined || value === "") return "—";
  const dict = DICTIONARIES[kind];
  return (dict && dict[value]) || humanise(value);
}

export default labelFor;
